import React, { useEffect, useState } from 'react'
import "./CSVUploadContainer.scss";
import { Close, CloudUpload } from '@livingdesign/icons';
import { useCampaignContext } from '../../../../../../../contexts/Campaign';
import { useFileUpload } from '../../../../../../../hooks/useFileUpload';
import { checkValidCsv, uploadCampaignCsv, uploadCampaignCsvSync } from '../../../../../../../utils/campaign';
import { CAMPAIGN, RECIPIENTS } from '../../../../../../../consts/DBFields';
import _ from 'lodash';
import csvThumbnail from "../../../../../../../assets/svg/csv-thumb.svg";
import { useMessageDetailsForm } from '../../../../../../../contexts/MessageDetailsForm';
import { IconButton, Spinner } from '@livingdesign/react';
import { getCsvFilename, updateRecipientField } from '../../../../../../../utils/campaignEdit';
import UploadProgressItem from '../../FormComponents/UploadProgressItem';
import { DROPZONE_SIZE_ERROR } from '../../../../../../../consts/Common';

export const QA_ID_PREFIX = "CSVUploadContainer";

function CSVUploadContainer({
    // csvFiles,
    maxFiles,
    accept,
    sizeLimit,
    multiple,
    id,
    onChange,
    clearError,
    getTotalFileSize = () => {},
    onError,
}) {
    
    const {
        formValues,
        setValue,
        filesError,
        setFilesError,
        deleteCsvFileV1,
        uploadingFilesList,
        setUploadingFilesList,
        uploadingFilesProgress,
        setUploadingFilesProgress,
        uploadPageLoading,
        setUploadPageLoading,
        csvDeleteInProgress,
        notificationIndex,
    } = useMessageDetailsForm();

    const [error, setError] = useState(false); // a SAS upload error
    const [sizeRejectedFiles, setSizeRejectedFiles] = useState([]);

    // const [csvFilesList, setCsvFilesList] = useState(formValues.csvFiles); // List of successfully uploaded/already existing CSV files

    const {
        selectedCampaign,
        saveCampaign,
        // updateDataSourceHeadingsV2,
        updateDataSourceHeadings,
        setCsvFile,
    } = useCampaignContext();

    const {
        isUploading,
        fileUploaderError,
        setFileUploaderError,
        // fileNames,
        getRootProps,
        getInputProps,
    } = useFileUpload({
        files: formValues.csvFiles,
        maxFiles,
        accept,
        sizeLimit,
        onRejected: (events) => {
            const sizeRejected = events.filter(e =>
                e.errors.some(err => err.code === DROPZONE_SIZE_ERROR)
            );
            setSizeRejectedFiles(sizeRejected.map(e => e.file.name));
        },
        handleUpload: async (files) => {
            let _filesError = {};
            let _csvFilesList = formValues.csvFiles;
            let _uploadingFilesList = {};
            let _uploadingFilesProgress = {};
            const uploadPromiseList = [];
            setUploadPageLoading(true);
            
            for(const file of files) {
                const actualFilename = file.name;
                const fileName = `${selectedCampaign[CAMPAIGN.UID]}.csv`;//`${selectedCampaign[CAMPAIGN.UID]}-${Date.now()}.csv`;

                //Check if a file with similar actualFilename exists
                const fileWithSameNameAlreadyExists = _csvFilesList.find(f => f.actualFilename === actualFilename);
                if(fileWithSameNameAlreadyExists) {
                    _filesError[fileName] = {
                        name: fileName,
                        actualFilename,
                        message: `File with name '${actualFilename}' already exists!!`
                    };
                    continue;
                }

                //Validate csv file
                const validateCsvHeadersResult = await checkValidCsv(file);
                const headersErrorMsg = `Please remove ',' or '.' from "${validateCsvHeadersResult.invalidHeaders}" column in the csv file and re-upload it!!`;
                if (!validateCsvHeadersResult.isValid) {
                    _filesError[fileName] = {
                        name: fileName,
                        actualFilename,
                        message: headersErrorMsg
                    };
                    continue;
                }
                
                //set uploading file data
                const uploadingFile = {
                    name: fileName,
                    actualFilename,
                    file,
                };
                _uploadingFilesList[fileName]= uploadingFile;

                //set initial upload progress
                _uploadingFilesProgress[fileName] = 0;

                const uploadPromise = uploadCampaignCsvSync(
                    selectedCampaign,
                    file,
                    async () => {
                        const newFile = {
                            name: fileName,
                            actualFilename,
                        }
                        _csvFilesList.push(newFile);
                        uploadingFile.done = true;
                        setUploadingFilesList({
                            ...uploadingFilesList,
                            [fileName]: uploadingFile
                        })
                        return file;
                    }, //success handler
                    (error) => {
                        _filesError[fileName] = {
                            name: fileName,
                            actualFilename: actualFilename,
                            message: error["message"]
                        };
                    }, //error handler,
                    null,
                    setUploadingFilesProgress,
                );
                uploadPromiseList.push(uploadPromise);
            }
            setUploadingFilesList(_.cloneDeep(_uploadingFilesList));
            setUploadingFilesProgress(_uploadingFilesProgress);
            await Promise.all(uploadPromiseList);
            
            let updatedCampaign = _.cloneDeep(selectedCampaign);

            updatedCampaign["general_data_source_type"] = updatedCampaign?.data_source?.type === "csv" ? null : "csv";
            const recipientFields = [RECIPIENTS.TO, RECIPIENTS.CC, RECIPIENTS.BCC];
            for(let recipientField of recipientFields) {
                updatedCampaign = updateRecipientField(
                    updatedCampaign,
                    notificationIndex,
                    recipientField,
                    null,
                );
                setValue(recipientField, null);
            }

            await saveCampaign(updatedCampaign, false);

            await updateDataSourceHeadings({ ...updatedCampaign }, false);
            setCsvFile(files?.[0]);

            setFilesError(_filesError);
            setUploadPageLoading(false);
        },
        clearError,
        multiple,
        getTotalFileSize,
        // fileUploadStateRef,
        initialError: error
    });

    useEffect(() => {
        if (onError) {
            if (fileUploaderError) {
                onError({
                    message: fileUploaderError,
                    isSizeError: sizeRejectedFiles.length > 0,
                    sizeLimitMB: sizeLimit ? Math.round(sizeLimit / 1000000) : null,
                });
            } else {
                onError(null);
                setSizeRejectedFiles([]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileUploaderError, onError]); // sizeRejectedFiles intentionally omitted — always updated in the same batch as fileUploaderError

    useEffect(() => {
        return () => {
            setFilesError({});
        }
    }, []);
    
    return (
        <>
            <div data-testid={`${QA_ID_PREFIX}-wrapper`}>
                {(isUploading || uploadPageLoading) &&
                    <div className='csv-upload-container-loading' data-testid={`${QA_ID_PREFIX}-loading-container`}>
                        {isUploading && <Spinner size='large' data-testid={`${QA_ID_PREFIX}-loading-spinner`} />}
                    </div>
                }
                <div className='csv-upload-container' {...getRootProps()} data-testid={`${QA_ID_PREFIX}-upload-area`}>
                    <input {...getInputProps()} name={`${id}-CSV-Input`} role="CSV Upload" data-testid={`${QA_ID_PREFIX}-file-input`}/>
                    <div className='upload-icon' data-testid={`${QA_ID_PREFIX}-upload-icon`}><CloudUpload width={64} height={64} color='#2E2F32' /></div>
                    <div className='upload-text' data-testid={`${QA_ID_PREFIX}-upload-text`}>Drag and drop your files here</div>
                    <div className='upload-instructions' data-testid={`${QA_ID_PREFIX}-upload-instructions`}>You can upload .csv files up to {sizeLimit ? `${Math.round(sizeLimit / 1000000)} MB` : '10 MB'}.</div>
                    <div style={{color: "#515357", padding: "8px 0"}} data-testid={`${QA_ID_PREFIX}-or-text`}>OR</div>
                    <div className='upload-button' data-testid={`${QA_ID_PREFIX}-browse-button`}>Browse files</div>
                </div>
            </div>
            {sizeRejectedFiles.length > 0 &&
                <div className="csv-files-list-container" data-testid={`${QA_ID_PREFIX}-size-rejected-container`}>
                    {sizeRejectedFiles.map((fileName, index) => (
                        <div key={fileName} className="size-rejected-item" data-testid={`${QA_ID_PREFIX}-size-rejected-item-${index}`}>
                            <img
                                style={{ height: "30px", width: "30px", flexShrink: 0 }}
                                alt={fileName}
                                src={csvThumbnail}
                                data-testid={`${QA_ID_PREFIX}-size-rejected-thumbnail-${index}`}
                            />
                            <div className="size-rejected-content" data-testid={`${QA_ID_PREFIX}-size-rejected-progress-wrapper-${index}`}>
                                <div className="file-upload-error-bar" data-testid={`${QA_ID_PREFIX}-size-rejected-bar-${index}`}></div>
                                <div className="size-rejected-bottom-row" data-testid={`${QA_ID_PREFIX}-size-rejected-bottom-${index}`}>
                                    <div className="size-rejected-filename" data-testid={`${QA_ID_PREFIX}-size-rejected-filename-${index}`}>{fileName}</div>
                                    <div className="error-message" data-testid={`${QA_ID_PREFIX}-size-rejected-message-${index}`}>Upload is too large</div>
                                </div>
                            </div>
                            <IconButton
                                a11yLabel="dismiss-size-rejected-file"
                                onClick={() => {
                                    setSizeRejectedFiles(prev => prev.filter(n => n !== fileName));
                                    setFileUploaderError("");
                                }}
                                data-testid={`${QA_ID_PREFIX}-size-rejected-dismiss-${index}`}
                            >
                                <Close size='medium' data-testid={`${QA_ID_PREFIX}-size-rejected-dismiss-icon-${index}`} />
                            </IconButton>
                        </div>
                    ))}
                </div>
            }
            {filesError && Object.keys(filesError).length > 0 &&
                <div className="file-upload-errors-container" data-testid={`${QA_ID_PREFIX}-errors-container`}>
                    {Object.keys(filesError).map((fileName, index) => {
                        const fileError = filesError[fileName];
                        return (
                            <div key={fileName} className="file-upload-error" data-testid={`${QA_ID_PREFIX}-error-item-${index}`}>
                                <img
                                    style={{
                                        height: "30px",
                                        width: "30px",
                                    }}
                                    id={`${id}-${QA_ID_PREFIX}-${index}-img`}
                                    alt={fileName}
                                    src={csvThumbnail}
                                    data-testid={`${QA_ID_PREFIX}-error-thumbnail-${index}`}
                                />
                                <div className="file-name" data-testid={`${QA_ID_PREFIX}-error-filename-${index}`}>{fileError.actualFilename}</div>
                                <div className="error-message" data-testid={`${QA_ID_PREFIX}-error-message-${index}`}>{fileError.message}</div>
                            </div>
                        )
                    })}
                </div>
            }
            <div className="csv-files-list-container" data-testid={`${QA_ID_PREFIX}-uploading-files-container`}>
                {Object.keys(uploadingFilesList).map((fileName) => {                    
                    const file = uploadingFilesList[fileName];
                    const uploadProgress = uploadingFilesProgress[fileName] || 0;
                    if(file.done) return null; // Skip files that are not being uploaded
                    return (
                        <UploadProgressItem
                            key={fileName}
                            id={`${id}-${QA_ID_PREFIX}-${fileName}`}
                            file={file}
                            progress={uploadProgress}
                            data-testid={`${QA_ID_PREFIX}-progress-item-${fileName}`}
                        />
                    )
                })}
            </div>
            <div className="csv-files-list-container" data-testid={`${QA_ID_PREFIX}-uploaded-files-container`}>
                {formValues?.csvFiles?.map((file, index) => {
                    const filename = getCsvFilename(file);
                    return (
                        <div key={filename} className="csv-files-list" data-testid={`${QA_ID_PREFIX}-uploaded-file-item-${index}`}>
                            <img
                                style={{
                                    height: "30px",
                                    width: "30px",
                                }}
                                alt={file.fileName}
                                src={csvThumbnail}
                                data-testid={`${QA_ID_PREFIX}-uploaded-file-thumbnail-${index}`}
                            />
                            <div  className="file-name" data-testid={`${QA_ID_PREFIX}-uploaded-file-name-${index}`}>{filename}</div>
                            <div data-testid={`${QA_ID_PREFIX}-uploaded-file-actions-${index}`}>
                                <IconButton
                                    disabled={isUploading || uploadPageLoading}
                                    a11yLabel="delete-csv-file-btn"
                                    onClick={ async () => {
                                        await deleteCsvFileV1(file.name);
                                    }}
                                    data-testid={`${QA_ID_PREFIX}-delete-button-${index}`}
                                >
                                    {(csvDeleteInProgress === file.name)
                                        ?
                                        <Spinner size='medium' data-testid={`${QA_ID_PREFIX}-delete-spinner-${index}`} />
                                        :
                                        <Close size='medium' data-testid={`${QA_ID_PREFIX}-delete-icon-${index}`} />
                                    }
                                </IconButton>
                            </div>
                        </div>
                    )
                })}
            </div>
            
        </>
    )
}

export default CSVUploadContainer;